import React, { useState, useEffect, useRef, FC } from 'react'
import CarouselV2 from 'components/SA/Carousel/v2'
import { CONSTRUCTOR, transformConstructorRecommendationsToProductCarousel } from '../helpers'
import ProductCarouselPolaroid from '../ProductCarouselPolaroid'
import useQA from 'hooks/useQA'
import useCarouselImageLazyLoading from 'components/SA/Carousel/v2/useCarouselImageLazyLoading'
import { SearchApi } from 'components/SA/SearchProvider/types'
import { ConstructorSearchService } from 'components/SA/SearchProvider/services/constructor/constructorService'
import { ConstructorResultsData } from 'components/SA/SearchProvider/constructorTypes'
import { ProductObject } from '../types'
import { useSelector } from 'react-redux'
import { getUserID } from 'selectors/user/session'

const MIN_ARTWORKS = 1
const GA_LIST_ID = 'popular-artworks'
const CONSTRUCTOR_RECOMMENDATION_POPULAR_POD_ID = 'popular-items-ap'
const DATA_API = CONSTRUCTOR

const SeeMoreArtworksCarousel: FC = () => {
  const [popularArtworks, setPopularArtworks] = useState<ReadonlyArray<ProductObject>>([])
  const [constructorResultId, setConstructorResultId] = useState('')
  const polaroidRef = useRef<HTMLDivElement>(null)
  const [polaroidWidth, setPolaroidWidth] = useState<number>(
    polaroidRef?.current?.offsetWidth || 311
  )
  const { handleImageLazyLoading, lastImageIndexLoaded } = useCarouselImageLazyLoading({
    offset: 5,
  })
  const userId = useSelector(getUserID)
  const searchService: SearchApi = new ConstructorSearchService({ userId: String(userId) })
  const { testId } = useQA()
  const LIST_NAME = 'Popular Artworks'

  const getSeeMoreArtworks = async () => {
    const seeMoreArtworksResponse = await searchService.fetchSearchProviderRecommendations<
      ConstructorResultsData
    >({
      podId: CONSTRUCTOR_RECOMMENDATION_POPULAR_POD_ID,
      filters: {
        availability_rank: ['4', '5'],
      },
      numResults: 48,
    })

    if (!seeMoreArtworksResponse.success) {
      setPopularArtworks([])
      return
    }

    const transformedSeeMoreArtworks = transformConstructorRecommendationsToProductCarousel({
      recommendationsResponse: seeMoreArtworksResponse.payload.data.recommendations,
    })
    setPopularArtworks(transformedSeeMoreArtworks)
    setConstructorResultId(seeMoreArtworksResponse.payload.data.resultId)
  }

  useEffect(() => {
    getSeeMoreArtworks()
  }, [])

  useEffect(() => {
    if (!!polaroidRef?.current?.offsetWidth) {
      setPolaroidWidth(polaroidRef?.current?.offsetWidth)
    }
  }, [polaroidRef?.current?.offsetWidth])

  if (!popularArtworks || popularArtworks.length < MIN_ARTWORKS) {
    return null
  }

  return (
    <div data-section={GA_LIST_ID} {...testId('see-more-artworks')}>
      <CarouselV2
        title={LIST_NAME}
        polaroidWidth={polaroidWidth}
        onScrollCallback={handleImageLazyLoading}
        searchProviderAttributes={[
          { 'recommendations-container': true },
          { 'recommendations-pod-id': CONSTRUCTOR_RECOMMENDATION_POPULAR_POD_ID },
          { 'result-id': constructorResultId },
          { 'total-number-results': popularArtworks.length },
        ]}
      >
        {popularArtworks.map((artwork, index) => (
          <ProductCarouselPolaroid
            key={index}
            polaroidRef={polaroidRef}
            artwork={artwork}
            index={index}
            listId={GA_LIST_ID}
            listName={LIST_NAME}
            dataApi={DATA_API}
            showArtistName={false}
            isLazyLoading={index > lastImageIndexLoaded}
            imageSuffix=''
            searchProviderAttributes={[{ item: 'recommendation' }]}
            showPrintsFromInPrice={artwork?.isPrintProduct}
            showDimensions={!artwork?.isPrintProduct}
            showPrintsQuantity={artwork?.isPrintProduct}
          />
        ))}
      </CarouselV2>
    </div>
  )
}

export default SeeMoreArtworksCarousel
